import { useState, useEffect, useRef, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  Calendar,
  User,
  Shield,
  Database,
  Globe,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  Eye,
  EyeOff,
  Clock,
  Monitor,
  MapPin,
  ArrowRightLeft,
  FileJson,
  ArrowRight,
  Minus,
  Plus,
} from 'lucide-react';
import { AuditoriaService, type AuditoriaRecord, type FiltrosAuditoria } from '../../services/auditoria/auditoria.service';
import { usePermissions } from '../../hooks/usePermissions';

/* ─── Helpers ─── */
const formatDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const badgeAccion = (accion: string) => {
  const map: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    INICIO_SESION: { bg: 'bg-emerald-100 text-emerald-700', text: 'Inicio Sesión', icon: <Shield className="w-3 h-3 mr-1" /> },
    CIERRE_SESION: { bg: 'bg-gray-100 text-gray-700', text: 'Cierre Sesión', icon: <Shield className="w-3 h-3 mr-1" /> },
    CREAR: { bg: 'bg-blue-100 text-blue-700', text: 'Crear', icon: <ArrowRightLeft className="w-3 h-3 mr-1" /> },
    ACTUALIZAR: { bg: 'bg-amber-100 text-amber-700', text: 'Actualizar', icon: <ArrowRightLeft className="w-3 h-3 mr-1" /> },
    ELIMINAR: { bg: 'bg-red-100 text-red-700', text: 'Eliminar', icon: <ArrowRightLeft className="w-3 h-3 mr-1" /> },
    VER: { bg: 'bg-sky-100 text-sky-700', text: 'Ver', icon: <Eye className="w-3 h-3 mr-1" /> },
    EXPORTAR: { bg: 'bg-purple-100 text-purple-700', text: 'Exportar', icon: <FileJson className="w-3 h-3 mr-1" /> },
    IMPORTAR: { bg: 'bg-indigo-100 text-indigo-700', text: 'Importar', icon: <FileJson className="w-3 h-3 mr-1" /> },
    IMPRIMIR: { bg: 'bg-pink-100 text-pink-700', text: 'Imprimir', icon: <Monitor className="w-3 h-3 mr-1" /> },
    BACKUP: { bg: 'bg-teal-100 text-teal-700', text: 'Backup', icon: <Database className="w-3 h-3 mr-1" /> },
    CIERRE_CAJA: { bg: 'bg-orange-100 text-orange-700', text: 'Cierre Caja', icon: <Calendar className="w-3 h-3 mr-1" /> },
    OTRO: { bg: 'bg-gray-100 text-gray-600', text: 'Otro', icon: <ArrowRightLeft className="w-3 h-3 mr-1" /> },
  };
  const style = map[accion] || map.OTRO;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${style.bg}`}>
      {style.icon}
      {style.text}
    </span>
  );
};

const badgeResultado = (resultado: string | null) => {
  if (!resultado) return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-500">—</span>;
  if (resultado === 'EXITO') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Éxito
      </span>
    );
  }
  if (resultado === 'ERROR') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-red-100 text-red-700">
        <XCircle className="w-3 h-3 mr-1" />
        Error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-700">
      <AlertTriangle className="w-3 h-3 mr-1" />
      {resultado}
    </span>
  );
};

/* ─── Componente para mostrar JSON en detalle ─── */
function JsonBlock({ title, data }: { title: string; data: Record<string, unknown> | null }) {
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div className="mt-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{title}</p>
      <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700 overflow-x-auto max-h-48">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

/* ─── Helpers para normalizar y renderizar valores ─── */
function tryParseJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try { return JSON.parse(trimmed); } catch { /* fall through */ }
  }
  return value;
}

function normalizeForCompare(value: unknown): unknown {
  const parsed = tryParseJson(value);
  if (typeof parsed === 'string') {
    // Normalizar strings vacíos y null string
    if (parsed === '' || parsed === 'null') return null;
    // Intentar parsear números
    const num = Number(parsed);
    if (!isNaN(num) && String(num) === parsed) return num;
  }
  return parsed;
}

function valuesAreEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalizeForCompare(a)) === JSON.stringify(normalizeForCompare(b));
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'number') return value.toLocaleString('es-CO');
  if (typeof value === 'string') {
    if (value === '') return '—';
    if (value.length > 200) return value.slice(0, 200) + '…';
    return value;
  }
  return JSON.stringify(value);
}

function isComplex(value: unknown): boolean {
  return typeof value === 'object' && value !== null;
}

/* ─── Componente para mostrar diff visual ─── */
function DiffBlock({ anterior, nuevo }: { anterior: Record<string, unknown> | null; nuevo: Record<string, unknown> | null }) {
  const [showAll, setShowAll] = useState(false);

  if (!anterior && !nuevo) return null;
  if (!anterior) {
    return (
      <div className="mt-3">
        <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide mb-2 flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Creación
        </p>
        <JsonBlock title="Datos" data={nuevo} />
      </div>
    );
  }
  if (!nuevo) {
    return (
      <div className="mt-3">
        <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2 flex items-center gap-1">
          <Minus className="w-3.5 h-3.5" /> Eliminación
        </p>
        <JsonBlock title="Datos" data={anterior} />
      </div>
    );
  }

  // Solo comparar campos que existen en 'nuevo' (ignorar metadatos que no se enviaron)
  const keysNuevo = Object.keys(nuevo);
  const cambios: { key: string; tipo: 'modificado' | 'agregado'; prev?: unknown; next?: unknown }[] = [];

  for (const key of keysNuevo) {
    const hasPrev = key in anterior;
    if (!hasPrev) {
      cambios.push({ key, tipo: 'agregado', next: nuevo[key] });
    } else if (!valuesAreEqual(anterior[key], nuevo[key])) {
      cambios.push({ key, tipo: 'modificado', prev: anterior[key], next: nuevo[key] });
    }
  }

  // Si showAll, también mostrar metadatos del anterior que no están en nuevo (info contextual)
  const metaKeys = Object.keys(anterior).filter(k => !(k in nuevo) && !k.startsWith('_'));

  if (cambios.length === 0 && !showAll) {
    return (
      <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs text-gray-500">Sin cambios detectados en los campos enviados</p>
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cambios realizados</p>
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-[10px] text-blue-600 hover:text-blue-700 font-medium underline"
        >
          {showAll ? 'Ver solo cambios' : 'Ver campos completos'}
        </button>
      </div>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {cambios.map((c, idx) => {
          const isPrevComplex = isComplex(normalizeForCompare(c.prev));
          const isNextComplex = isComplex(normalizeForCompare(c.next));
          return (
            <div key={c.key} className={`px-3 py-2 ${idx !== cambios.length - 1 ? 'border-b border-gray-100' : ''}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{c.key}</span>
                {c.tipo === 'modificado' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Modificado</span>}
                {c.tipo === 'agregado' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Nuevo</span>}
              </div>
              {c.tipo === 'modificado' && !isPrevComplex && !isNextComplex && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-red-600 bg-red-50 px-2 py-1 rounded line-through">{formatValue(c.prev)}</span>
                  <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" />
                  <span className="text-emerald-700 bg-emerald-50 px-2 py-1 rounded font-medium">{formatValue(c.next)}</span>
                </div>
              )}
              {c.tipo === 'modificado' && (isPrevComplex || isNextComplex) && (
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] text-gray-400 mb-0.5">Anterior</p>
                    <pre className="bg-red-50 border border-red-100 rounded p-2 text-red-700 overflow-x-auto max-h-32 text-[10px]">{JSON.stringify(normalizeForCompare(c.prev), null, 2)}</pre>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 mb-0.5">Nuevo</p>
                    <pre className="bg-emerald-50 border border-emerald-100 rounded p-2 text-emerald-700 overflow-x-auto max-h-32 text-[10px]">{JSON.stringify(normalizeForCompare(c.next), null, 2)}</pre>
                  </div>
                </div>
              )}
              {c.tipo === 'agregado' && !isNextComplex && (
                <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded font-medium">{formatValue(c.next)}</span>
              )}
              {c.tipo === 'agregado' && isNextComplex && (
                <pre className="bg-emerald-50 border border-emerald-100 rounded p-2 text-emerald-700 overflow-x-auto max-h-32 text-[10px]">{JSON.stringify(normalizeForCompare(c.next), null, 2)}</pre>
              )}
            </div>
          );
        })}

        {showAll && metaKeys.map((key, idx) => (
          <div key={`meta-${key}`} className={`px-3 py-2 text-xs text-gray-400 ${idx !== metaKeys.length - 1 || cambios.length > 0 ? 'border-t border-gray-100' : ''}`}>
            <span className="font-medium text-gray-500">{key}:</span>{' '}
            <span className="italic">{formatValue(anterior[key])}</span>
            <span className="ml-1 text-[10px] text-gray-300">(sin cambio)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Página Principal ─── */
export function AuditoriaPage() {
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<FiltrosAuditoria>({});
  const [tempFilters, setTempFilters] = useState<FiltrosAuditoria>({});

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['auditoria', page, limit, filters],
    queryFn: () => AuditoriaService.listar({ ...filters, pagina: page, limite: limit, orden: 'desc' }),
    placeholderData: (prev) => prev,
  });

  const records = data?.data ?? [];
  const meta = data?.meta;

  const applyFilters = () => {
    setPage(1);
    setFilters({ ...tempFilters });
  };

  const clearFilters = () => {
    setTempFilters({});
    setFilters({});
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some((v) => v !== undefined && v !== '');

  const handlePageChange = (newPage: number) => {
    if (meta && newPage >= 1 && newPage <= meta.totalPaginas) {
      setPage(newPage);
    }
  };

  // Reset page cuando cambian filtros globales
  useEffect(() => {
    setPage(1);
  }, [filters.accion, filters.modulo, filters.entidad, filters.resultado]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auditoría del Sistema</h1>
            <p className="mt-1 text-sm text-gray-500">
              Registro de acciones, inicios de sesión y operaciones realizadas por los usuarios.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters((s) => !s)}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
              {hasActiveFilters && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-white text-emerald-600 rounded-full">
                  {Object.values(filters).filter((v) => v !== undefined && v !== '').length}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        {/* Filtros */}
        {showFilters && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Fecha Desde */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  Fecha desde
                </label>
                <input
                  type="datetime-local"
                  value={tempFilters.fechaDesde ?? ''}
                  onChange={(e) => setTempFilters((f) => ({ ...f, fechaDesde: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
              {/* Fecha Hasta */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  Fecha hasta
                </label>
                <input
                  type="datetime-local"
                  value={tempFilters.fechaHasta ?? ''}
                  onChange={(e) => setTempFilters((f) => ({ ...f, fechaHasta: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
              {/* Usuario Email */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <User className="w-3 h-3 inline mr-1" />
                  Email usuario
                </label>
                <input
                  type="text"
                  placeholder="ej: admin@farmacia.com"
                  value={tempFilters.emailUsuario ?? ''}
                  onChange={(e) => setTempFilters((f) => ({ ...f, emailUsuario: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                />
              </div>
              {/* Acción */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <Shield className="w-3 h-3 inline mr-1" />
                  Acción
                </label>
                <select
                  value={tempFilters.accion ?? ''}
                  onChange={(e) => setTempFilters((f) => ({ ...f, accion: e.target.value || undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                >
                  <option value="">Todas</option>
                  <option value="INICIO_SESION">Inicio Sesión</option>
                  <option value="CIERRE_SESION">Cierre Sesión</option>
                  <option value="CREAR">Crear</option>
                  <option value="ACTUALIZAR">Actualizar</option>
                  <option value="ELIMINAR">Eliminar</option>
                  <option value="VER">Ver</option>
                  <option value="EXPORTAR">Exportar</option>
                  <option value="IMPORTAR">Importar</option>
                  <option value="IMPRIMIR">Imprimir</option>
                  <option value="BACKUP">Backup</option>
                  <option value="CIERRE_CAJA">Cierre Caja</option>
                </select>
              </div>
              {/* Módulo */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <Monitor className="w-3 h-3 inline mr-1" />
                  Módulo
                </label>
                <select
                  value={tempFilters.modulo ?? ''}
                  onChange={(e) => setTempFilters((f) => ({ ...f, modulo: e.target.value || undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                >
                  <option value="">Todos</option>
                  <option value="Autenticación">Autenticación</option>
                  <option value="Productos">Productos</option>
                  <option value="Pedidos">Pedidos</option>
                  <option value="Proveedores">Proveedores</option>
                  <option value="Administradores">Administradores</option>
                  <option value="Cierre de Caja">Cierre de Caja</option>
                  <option value="Dashboard">Dashboard</option>
                  <option value="Reportes">Reportes</option>
                </select>
              </div>
              {/* Entidad */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <Database className="w-3 h-3 inline mr-1" />
                  Entidad
                </label>
                <select
                  value={tempFilters.entidad ?? ''}
                  onChange={(e) => setTempFilters((f) => ({ ...f, entidad: e.target.value || undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                >
                  <option value="">Todas</option>
                  <option value="Usuario">Usuario</option>
                  <option value="Producto">Producto</option>
                  <option value="Presentacion">Presentación</option>
                  <option value="Lote">Lote</option>
                  <option value="Stock">Stock</option>
                  <option value="Pedido">Pedido</option>
                  <option value="Proveedor">Proveedor</option>
                  <option value="CierreCaja">Cierre Caja</option>
                </select>
              </div>
              {/* Resultado */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                  Resultado
                </label>
                <select
                  value={tempFilters.resultado ?? ''}
                  onChange={(e) => setTempFilters((f) => ({ ...f, resultado: e.target.value || undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white"
                >
                  <option value="">Todos</option>
                  <option value="EXITO">Éxito</option>
                  <option value="ERROR">Error</option>
                  <option value="ADVERTENCIA">Advertencia</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
              >
                Limpiar
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Aplicar filtros
              </button>
            </div>
          </div>
        )}

        {/* Tabla */}
        <div className="relative flex-1 overflow-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 w-10"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Fecha / Hora
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Usuario
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Acción
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Módulo / Entidad
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Endpoint
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Resultado
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading && records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Loader2 className="w-8 h-8 text-emerald-600 animate-spin mb-3" />
                      <p className="text-sm text-gray-500">Cargando registros de auditoría...</p>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Search className="w-12 h-12 text-gray-300 mb-3" />
                      <p className="text-gray-500">No se encontraron registros</p>
                      <p className="text-gray-400 text-sm">
                        {hasActiveFilters
                          ? 'Intenta ajustar los filtros de búsqueda.'
                          : 'Aún no hay registros de auditoría en el sistema.'}
                      </p>
                      {hasActiveFilters && (
                        <button
                          onClick={clearFilters}
                          className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                        >
                          Limpiar filtros
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                records.map((record) => {
                  const isExpanded = expandedRow === record.id;
                  return (
                    <Fragment key={record.id}>
                      <tr
                        className={`hover:bg-gray-100 transition-colors cursor-pointer group ${
                          isExpanded ? 'bg-emerald-50/30' : ''
                        }`}
                        onClick={() => setExpandedRow(isExpanded ? null : record.id)}
                      >
                        <td className="px-4 py-4">
                          <button className="p-1 rounded hover:bg-gray-200 transition-colors">
                            {isExpanded ? (
                              <EyeOff className="w-4 h-4 text-gray-400" />
                            ) : (
                              <Eye className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-sm text-gray-900 font-medium">
                              {formatDate(record.creadoEn)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              {record.nombreUsuario || '—'}
                            </span>
                            {record.emailUsuario && (
                              <span className="text-xs text-gray-500">{record.emailUsuario}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">{badgeAccion(record.accion)}</td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-0.5">
                            {record.modulo && (
                              <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded inline-flex items-center w-fit">
                                <Monitor className="w-3 h-3 mr-1 text-gray-500" />
                                {record.modulo}
                              </span>
                            )}
                            {record.entidad && (
                              <span className="text-xs text-gray-500">
                                {record.entidad}
                                {record.entidadId && (
                                  <span className="ml-1 text-gray-400 font-mono">{record.entidadId.slice(0, 8)}...</span>
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1.5">
                            <Globe className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-xs text-gray-600 font-mono truncate max-w-[180px]">
                              {record.metodoHttp || ''} {record.endpoint || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">{badgeResultado(record.resultado)}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50/50">
                          <td colSpan={7} className="px-6 py-5 border-b border-gray-100">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-3">Detalles técnicos</h4>
                                <div className="space-y-2 text-sm">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                      <span className="text-gray-500">IP:</span>{' '}
                                      <span className="font-mono text-gray-700">{record.ipAddress || '—'}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Monitor className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div className="break-all">
                                      <span className="text-gray-500">User-Agent:</span>{' '}
                                      <span className="text-gray-700 text-xs">{record.userAgent || '—'}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Database className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                      <span className="text-gray-500">Entidad ID:</span>{' '}
                                      <span className="font-mono text-gray-700">{record.entidadId || '—'}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <User className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                      <span className="text-gray-500">Usuario ID:</span>{' '}
                                      <span className="font-mono text-gray-700">{record.usuarioId || '—'}</span>
                                    </div>
                                  </div>
                                </div>
                                {record.mensajeError && (
                                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                                      Error
                                    </p>
                                    <p className="text-sm text-red-600">{record.mensajeError}</p>
                                  </div>
                                )}
                              </div>
                              <div>
                                <DiffBlock anterior={record.datosAnteriores} nuevo={record.datosNuevos} />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Loading indicator */}
          {isFetching && records.length > 0 && (
            <div className="absolute top-2 right-2 z-10">
              <div className="bg-white rounded-full shadow-md border border-gray-100 p-2 flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
                <span className="text-xs font-medium text-gray-600">Buscando...</span>
              </div>
            </div>
          )}
        </div>

        {/* Paginación */}
        {meta && meta.total > 0 && (
          <div className="px-6 py-6 border-t border-gray-100 bg-gray-50/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="text-sm text-gray-600">
                  <span className="font-medium text-gray-900">
                    {(meta.pagina - 1) * meta.limite + 1}
                  </span>
                  <span className="mx-1">–</span>
                  <span className="font-medium text-gray-900">
                    {Math.min(meta.pagina * meta.limite, meta.total)}
                  </span>
                  <span className="mx-2">de</span>
                  <span className="font-semibold text-gray-900">{meta.total}</span>
                  <span className="ml-1">registros</span>
                </div>
                <div className="text-sm text-gray-500">
                  Página <span className="font-medium text-gray-900">{meta.pagina}</span> de{' '}
                  <span className="font-medium text-gray-900">{meta.totalPaginas}</span>
                </div>
              </div>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={meta.pagina <= 1}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    meta.pagina > 1
                      ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="Primera página"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePageChange(meta.pagina - 1)}
                  disabled={meta.pagina <= 1}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    meta.pagina > 1
                      ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center space-x-1 mx-2">
                  {(() => {
                    const pages: React.ReactNode[] = [];
                    const maxVisiblePages = 3;
                    let start = Math.max(1, meta.pagina - Math.floor(maxVisiblePages / 2));
                    let end = Math.min(meta.totalPaginas, start + maxVisiblePages - 1);
                    start = Math.max(1, end - maxVisiblePages + 1);

                    if (start > 1) {
                      pages.push(
                        <button
                          key="1"
                          onClick={() => handlePageChange(1)}
                          className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                        >
                          1
                        </button>
                      );
                      if (start > 2) {
                        pages.push(
                          <span key="ellipsis-start" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                        );
                      }
                    }

                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all ${
                            i === meta.pagina
                              ? 'bg-emerald-600 text-white shadow-sm'
                              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    if (end < meta.totalPaginas) {
                      if (end < meta.totalPaginas - 1) {
                        pages.push(
                          <span key="ellipsis-end" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                        );
                      }
                      pages.push(
                        <button
                          key={meta.totalPaginas}
                          onClick={() => handlePageChange(meta.totalPaginas)}
                          className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                        >
                          {meta.totalPaginas}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <button
                  onClick={() => handlePageChange(meta.pagina + 1)}
                  disabled={meta.pagina >= meta.totalPaginas}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    meta.pagina < meta.totalPaginas
                      ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="Página siguiente"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePageChange(meta.totalPaginas)}
                  disabled={meta.pagina >= meta.totalPaginas}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                    meta.pagina < meta.totalPaginas
                      ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      : 'text-gray-300 cursor-not-allowed'
                  }`}
                  title="Última página"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
